import { FundSIFSuitability, RiskCategory, SIFSchemesWithReturns } from "@/type/sifPageTypes";
import Icon from "../icons/Icons";
import Tooltip from "../ui/Tooltip";
import { normalizeRiskCategory } from "@/utils/commonFunctions";

type RiskLevel = "very low" | "low" | "medium" | "moderate to high" | "high" | "very high";

type SIFInvestmentRiskStepProps = {
  suitability: FundSIFSuitability | null;
  fundsData: SIFSchemesWithReturns;
};

const riskLabelMap: Record<RiskLevel, string> = {
  "very low": "VERY LOW",
  low: "LOW",
  medium: "MEDIUM",
  "moderate to high": "MOD-HIGH",
  high: "HIGH",
  "very high": "VERY HIGH",
};

const riskOrder: RiskCategory[] = ["very low", "low", "medium", "moderate to high", "high", "very high"];

const riskColorMap: Record<RiskCategory, string> = {
  "very low": "bg-[#30AD43] text-[#30AD43]",
  low: "bg-[#5ECB3B] text-[#5ECB3B]",
  medium: "bg-[#F5C02E] text-[#F5C02E]",
  "moderate to high": "bg-[#FF8B3D] text-[#FF8B3D]",
  high: "bg-[#F95B4E] text-[#F95B4E]",
  "very high": "bg-[#E43E3E] text-[#E43E3E]",
};

export default function SIFInvestmentRiskStep({ suitability, fundsData }: SIFInvestmentRiskStepProps) {
  const riskValue = normalizeRiskCategory(fundsData.scheme_risk || "");

  const activeIndex = riskOrder.indexOf(riskValue ?? "medium"); // Default to "medium" if not found

  return (
    <>
      <div className="flex flex-col xs:flex-row items-stretch gap-5 mb-4 sm:mb-6">
        {/* Risk Analysis */}
        <div className="w-full rounded-2xl border border-white/10 p-4 bg-white/5">
          <p className="font-bold leading-1.2 mb-4">Risk Analysis</p>

          <div className="flex justify-between text-xs font-bold text-[#99A1AF] mb-3">
            <span>LOWER RISK</span>
            <span>HIGHER RISK</span>
          </div>

          <div className="flex items-center gap-2 mb-5">
            {riskOrder.map((level, index) => {
              const isActive = index === activeIndex;

              return (
                <div
                  key={level}
                  className={`relative flex-1 h-2 rounded-full transition-all duration-300 ${
                    isActive ? riskColorMap[level] : "bg-white/10"
                  }`}
                >
                  {isActive && (
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 rounded-full bg-white border-2 border-black" />
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between bg-white/10 rounded-2lg px-3 py-2.5 mb-4">
            <span className="text-sm text-[#D1D5DC]">Risk Profile :</span>
            <span className={`${riskColorMap[riskValue]} bg-transparent! leading-1.2 font-bold`}>
              {riskLabelMap[riskValue]}
            </span>
          </div>

          <p className="text-xs leading-1.5 text-[#99A1AF] max-h-20 overflow-y-auto scrollbar-xs pr-1.5">
            {suitability?.suitability}
          </p>
        </div>

        {/* Investor Suitability */}
        <div className="w-full rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
          <div className="max-h-64 overflow-y-auto scrollbar-xs p-4">
            {suitability?.type && (
              <>
                <p className="font-bold leading-1.2 mb-4">Scheme Type</p>
                <p className="text-xs leading-1.5 text-[#99A1AF] mb-4 sm:mb-6">{suitability?.type}</p>
              </>
            )}

            {suitability?.min_application && (
              <>
                <p className="font-bold leading-1.2 mb-4">Minimum Application Amount</p>
                <p className="text-xs leading-1.5 text-[#99A1AF]">{suitability?.min_application}</p>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 p-4 bg-white/5">
        <p className="font-bold leading-1.2 mb-3 sm:mb-4">Fund Terms</p>

        <div className="space-y-2.5 sm:space-y-3">
          {fundsData.benchmark && (
            <div className="flex items-center justify-between gap-4">
              <p className="text-2xs xs:text-sm text-[#99A1AF]">Benchmark Index</p>
              <p className="text-sm xs:text-base font-medium text-white text-right">{fundsData.benchmark}</p>
            </div>
          )}

          <div className="flex items-center justify-between">
            <p className="text-2xs xs:text-sm text-[#99A1AF] shrink-0">Exit Load</p>

            <div className="flex items-center justify-end gap-2">
              <span className="sm:max-w-[60%] text-sm xs:text-base font-medium text-white text-right line-clamp-1">
                {fundsData.exit_load || "N/A"}
              </span>

              {fundsData.exit_load !== "None" && fundsData.exit_load !== "NIL" && fundsData.exit_load !== "" && (
                <Tooltip
                  content={fundsData.exit_load}
                  trigger="click"
                  position="top-right"
                  className="w-4 h-4 shrink-0"
                >
                  <button className="w-4 h-4">
                    <Icon.infoIcon className="w-4 h-4 rotate-180 transition text-white/70 hover:text-white" />
                  </button>
                </Tooltip>
              )}
            </div>
          </div>

          {/* <div className="flex items-center justify-between">
            <p className="text-2xs xs:text-sm text-[#99A1AF]">Taxation</p>
            <p className="text-sm xs:text-base font-medium text-white text-right">Remaining !!!</p>
          </div> */}
        </div>
      </div>
    </>
  );
}
